import {
  ArrowLeftIcon,
  CameraIcon,
  PhoneIcon,
  PhotographIcon,
  PlusCircleIcon,
  ThumbUpIcon,
} from '@heroicons/react/solid';
import type { ReactNode } from 'react';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import PhoneFrame from './PhoneFrame';
import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { delay } from '../../primitives';

export type MessengerLine =
  | { from: 'customer'; text: ReactNode; at: number }
  | { from: 'agent'; text: ReactNode; at: number; typingAt?: number };

/**
 * A customer's Messenger chat with a business's Facebook Page, in
 * Messenger's own look: blue outgoing bubbles, grey incoming ones and the
 * Page's picture beside its last message in a run.
 */
export default function MessengerChat({
  brand,
  lines,
}: {
  brand: DemoBrand;
  lines: MessengerLine[];
}) {
  const { t } = useChannelsT();

  return (
    <PhoneFrame bar='bg-white text-[#050505] dark:bg-[#000] dark:text-white'>
      <div className='flex items-center gap-2.5 border-b border-black/[0.06] bg-white px-3 pb-2.5 pt-1 text-[#050505] dark:border-white/[0.08] dark:bg-[#000] dark:text-white'>
        <ArrowLeftIcon className='h-5 w-5 flex-none text-[#0866ff] rtl:-scale-x-100' />
        <Avatar brand={brand} className='h-9 w-9 text-[15px]' />
        <span className='min-w-0 flex-1'>
          <span className='block truncate text-[15px] font-semibold leading-tight'>
            {brand.name}
          </span>
          <span className='block truncate text-[11.5px] leading-tight text-[#65676b] dark:text-[#a8abaf]'>
            {t('Typically replies instantly')}
          </span>
        </span>
        <PhoneIcon className='h-5 w-5 flex-none text-[#0866ff]' />
      </div>

      <div className='flex min-h-[25rem] flex-col justify-end gap-1 bg-white px-3 pb-2 pt-3 text-[13.5px] leading-[1.35] text-[#050505] dark:bg-[#000] dark:text-[#e4e6eb]'>
        <p
          className={clsxm(
            styles.enter,
            'mx-auto mb-2 text-[11px] font-medium text-[#65676b] dark:text-[#a8abaf]',
          )}
          style={delay(150)}
        >
          {t('Today')} 10:12
        </p>

        {lines.map((line, index) => {
          const next = lines[index + 1];
          const lastInRun = !next || next.from !== line.from;
          if (line.from === 'customer') {
            return (
              <div key={index} className='flex justify-end'>
                <p
                  className={clsxm(
                    styles.bubble,
                    styles.bubbleEnd,
                    'max-w-[78%] rounded-[18px] bg-[#0866ff] px-3 py-2 text-white',
                    !lastInRun && 'rounded-ee-[5px]',
                  )}
                  style={delay(line.at)}
                >
                  {line.text}
                </p>
              </div>
            );
          }
          return (
            <div key={index} className='grid'>
              {line.typingAt !== undefined && (
                <span
                  className={clsxm(
                    styles.typing,
                    'ms-8 flex h-8 w-14 items-center justify-center gap-1 self-start rounded-[18px] bg-[#f0f0f0] [grid-area:1/1] dark:bg-[#303030]',
                  )}
                  style={delay(line.typingAt, {
                    '--len': `${line.at - line.typingAt}ms`,
                  })}
                >
                  <span className='h-1.5 w-1.5 rounded-full bg-[#90949c]' />
                  <span className='h-1.5 w-1.5 rounded-full bg-[#90949c]' />
                  <span className='h-1.5 w-1.5 rounded-full bg-[#90949c]' />
                </span>
              )}
              <div
                className={clsxm(
                  styles.bubble,
                  styles.bubbleStart,
                  'flex items-end gap-2 [grid-area:1/1]',
                )}
                style={delay(line.at)}
              >
                {lastInRun ? (
                  <Avatar brand={brand} className='h-6 w-6 text-[10px]' />
                ) : (
                  <span className='w-6 flex-none' />
                )}
                <p
                  className={clsxm(
                    'max-w-[78%] rounded-[18px] bg-[#f0f0f0] px-3 py-2 dark:bg-[#303030]',
                    !lastInRun && 'rounded-es-[5px]',
                  )}
                >
                  {line.text}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className='flex items-center gap-2.5 bg-white px-3 pb-4 pt-2 text-[#0866ff] dark:bg-[#000]'>
        <PlusCircleIcon className='h-6 w-6 flex-none' />
        <CameraIcon className='h-6 w-6 flex-none' />
        <PhotographIcon className='h-6 w-6 flex-none' />
        <span className='flex h-9 min-w-0 flex-1 items-center rounded-full bg-[#f0f2f5] px-3 text-[14px] text-[#65676b] dark:bg-[#303030] dark:text-[#a8abaf]'>
          Aa
        </span>
        <ThumbUpIcon className='h-6 w-6 flex-none' />
      </div>
    </PhoneFrame>
  );
}

function Avatar({
  brand,
  className,
}: {
  brand: DemoBrand;
  className?: string;
}) {
  return (
    <span
      className={clsxm(
        'flex flex-none items-center justify-center rounded-full font-semibold text-white',
        brand.bg,
        className,
      )}
    >
      {brand.initial}
    </span>
  );
}
